import { EyeIcon, ListBulletIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Center,
    Checkbox,
    Group,
    Loader,
    rem,
    SegmentedControl,
    Select,
    Table,
    Text,
    Tooltip,
} from "@mantine/core";
import { memo, SVGProps } from "react";
import { Link } from "react-router-dom";
import { ConnectedPersonActionsMenu } from "../../../components/common/PersonActionsMenu";
import { ROUTES } from "../../../constants/routes";
import { Person, SortDirection } from "../../../models/person.model";
import { isPersonCurrentlyUnassigned } from '../../../utils/availabilityUtils';
import { CVDownloadMenu } from "../../CVEditor/components/CVDownloadMenu";

const resolveImageSrc = (image: unknown): string | undefined => {
    if (typeof image !== "string" || !image) return undefined;
    if (image.startsWith("data:image")) return image;
    if (image.startsWith("http://") || image.startsWith("https://")) return image;
    // Raw base64
    return `data:image/jpeg;base64,${image}`;
};

const IconEye = (props: SVGProps<SVGSVGElement>) => <EyeIcon {...props} />;
const IconSquares2X2 = (props: SVGProps<SVGSVGElement>) => (
    <Squares2X2Icon {...props} />
);
const IconListBullet = (props: SVGProps<SVGSVGElement>) => (
    <ListBulletIcon {...props} />
);
const IconChevronUp = (props: SVGProps<SVGSVGElement>) => (
    <ChevronUpIcon {...props} />
);
const IconChevronDown = (props: SVGProps<SVGSVGElement>) => (
    <ChevronDownIcon {...props} />
);

interface CVListTableProps {
    persons: (Person & { lastUpdated: string })[];
    loading: boolean;
    searchDuration: number;
    sortField: string | null;
    sortDirection: SortDirection;
    currentKeywords: string[];
    pageSize: number;
    isFiltered: boolean;
    viewMode: "grid" | "list";
    onSortChange: (field: string) => void;
    onPageSizeChange: (value: string) => void;
    onViewModeChange: (value: string) => void;
    onSelectPerson: (person: Person) => void;
    onModifyPerson: (e: React.MouseEvent, personId: number) => void;
    onDeletePerson: (e: React.MouseEvent, person: Person) => void;
    onClearSearch: () => void;
    onRefresh?: () => void;
    selectionMode?: boolean;
    selectedIds?: Set<number>;
    onToggleSelect?: (id: number) => void;
}

interface CVListTableRowProps {
    person: Person & { lastUpdated: string };
    selectionMode: boolean;
    isSelected: boolean;
    onToggleSelect?: (id: number) => void;
    onSelectPerson: (person: Person) => void;
    onModifyPerson: (e: React.MouseEvent, personId: number) => void;
    onDeletePerson: (e: React.MouseEvent, person: Person) => void;
    onRefresh?: () => void;
}

const CVListTableRow = memo(({
    person,
    selectionMode,
    isSelected,
    onToggleSelect,
    onSelectPerson,
    onModifyPerson,
    onDeletePerson,
    onRefresh,
}: CVListTableRowProps) => (
    <Table.Tr
        key={person.id}
        onClick={() => {
            if (selectionMode) {
                onToggleSelect?.(person.id);
                return;
            }
            onSelectPerson(person);
        }}
        style={{
            cursor: "pointer",
            background: selectionMode && isSelected
                ? 'light-dark(var(--mantine-color-materialAmber-0), rgba(255, 193, 7, 0.14))'
                : undefined,
        }}
    >
        {selectionMode && (
            <Table.Td onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={isSelected}
                    onChange={() => onToggleSelect?.(person.id)}
                    aria-label={`Select ${person.firstName} ${person.lastName}`}
                />
            </Table.Td>
        )}
        <Table.Td>
            <Group gap="sm">
                <Avatar
                    size="md"
                    radius="xl"
                    color="brand"
                    variant="transparent"
                    src={resolveImageSrc(person.image)}
                >
                    {`${person.firstName.charAt(
                        0
                    )}${person.lastName.charAt(0)}`}
                </Avatar>
                <Text
                    fw={500}
                >{`${person.firstName} ${person.lastName}`}</Text>
            </Group>
        </Table.Td>
        <Table.Td>
            {isPersonCurrentlyUnassigned(person.availabilityDate) ? (
                <Badge color="orange">Unassigned</Badge>
            ) : (
                <Badge color="green">Assigned</Badge>
            )}
            {person.inactive && (
                <Badge color="gray.7" ml={5}>
                    Inactive
                </Badge>
            )}
        </Table.Td>
        <Table.Td>
            {person.availabilityDate &&
                new Date(person.availabilityDate).getFullYear() >
                1 ? (
                <Badge color="orange.9">
                    Available{" "}
                    {new Date(
                        person.availabilityDate
                    ).toLocaleDateString()}
                </Badge>
            ) : (
                <Text size="sm" c="dimmed">
                    Not specified
                </Text>
            )}
        </Table.Td>
        <Table.Td>{person.positions?.[0] || ""}</Table.Td>
        <Table.Td>
            {person.officeLocationName ? (
                <Text size="sm">📍 {person.officeLocationName}</Text>
            ) : (
                <Text size="sm" c="dimmed">
                    Not specified
                </Text>
            )}
        </Table.Td>
        <Table.Td>
            <Text size="sm" c="dimmed">
                CV last updated:
            </Text>
            <Text size="sm">{person.lastUpdated}</Text>
        </Table.Td>
        <Table.Td>
            {person.finalScoring !== undefined ? (
                <Badge
                    color={
                        person.finalScoring < 0.3
                            ? "red"
                            : person.finalScoring < 0.7
                                ? "orange"
                                : "green"
                    }
                    variant="filled"
                >
                    {Math.round(person.finalScoring * 100)}%
                </Badge>
            ) : (
                <Text size="sm" c="dimmed">
                    --
                </Text>
            )}
        </Table.Td>
        <Table.Td>
            {!selectionMode && (
                <Group gap="xs">
                    <Button
                        component={Link}
                        to={ROUTES.CV_DETAILS(person.id)}
                        variant="light"
                        color="gray"
                        size="sm"
                        leftSection={<IconEye width={16} />}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        title="Click to view, Ctrl+Click for new tab"
                    >
                        View
                    </Button>

                    <Box onClick={(e) => e.stopPropagation()}>
                        <CVDownloadMenu
                            personId={person.id}
                            userName={`${person.firstName} ${person.lastName}`}
                            buttonProps={{
                                size: "sm",
                                variant: "outline",
                                children: "Download",
                            }}
                        />
                    </Box>

                    <Box onClick={(e) => e.stopPropagation()}>
                        <ConnectedPersonActionsMenu
                            person={person}
                            isUnassigned={isPersonCurrentlyUnassigned(person.availabilityDate)}
                            isInactive={person.inactive}
                            onModify={(e) =>
                                onModifyPerson(e, person.id)
                            }
                            onDelete={(e) => onDeletePerson(e, person)}
                            onRefresh={onRefresh}
                            variant="icon"
                        />
                    </Box>
                </Group>
            )}
        </Table.Td>
    </Table.Tr>
), (prevProps, nextProps) => {
    return (
        prevProps.person === nextProps.person &&
        prevProps.selectionMode === nextProps.selectionMode &&
        prevProps.isSelected === nextProps.isSelected
    );
});

export function CVListTable({
    persons,
    loading,
    searchDuration,
    sortField,
    sortDirection,
    currentKeywords,
    pageSize,
    isFiltered,
    viewMode,
    onSortChange,
    onPageSizeChange,
    onViewModeChange,
    onSelectPerson,
    onModifyPerson,
    onDeletePerson,
    onClearSearch,
    onRefresh,
    selectionMode = false,
    selectedIds,
    onToggleSelect,
}: CVListTableProps) {

    const renderSortIndicator = (field: string) => {
        if (sortField !== field || field === "none") return null;

        return sortDirection === SortDirection.Ascending ? (
            <IconChevronUp style={{ width: rem(14), height: rem(14) }} />
        ) : (
            <IconChevronDown style={{ width: rem(14), height: rem(14) }} />
        );
    };

    const SortableTableHeader = ({
        field,
        label,
    }: {
        field: string;
        label: string;
    }) => (
        <Table.Th
            onClick={() => onSortChange(field)}
            style={{ cursor: "pointer" }}
        >
            <Group gap="xs" justify="apart">
                <Text>{label}</Text>
                {renderSortIndicator(field)}
            </Group>
        </Table.Th>
    );

    if (loading && persons.length === 0) {
        return (
            <Box p="xl" ta="center">
                <Loader size="md" color="brand" />
                <Text mt="md">Loading results...</Text>
                {searchDuration > 10 && (
                    <Text size="sm" c="dimmed" mt="xs">
                        This may take a moment. Search has been running for{" "}
                        {searchDuration} seconds.
                    </Text>
                )}
            </Box>
        );
    }

    if (persons.length === 0) {
        return (
            <Box p="xl" ta="center">
                <Text size="lg" fw={500} c="dimmed">
                    No CVs found matching your search criteria
                </Text>
                <Text size="sm" c="dimmed" mt="md">
                    Try adjusting your search terms
                </Text>
                <Group justify="center" mt="md">
                    <Button
                        variant="subtle"
                        color="brand"
                        size="xs"
                        onClick={onClearSearch}
                        disabled={currentKeywords.length === 0}
                    >
                        Clear search terms
                    </Button>
                </Group>
            </Box>
        );
    }

    return (
        <Box>
            {/* ── Page-size / view-toggle bar (table view) ── */}
            <Group mb="sm" justify="flex-end" gap="xs" align="flex-end">
                <Select
                    size="xs"
                    value={pageSize.toString()}
                    onChange={(value) => value && onPageSizeChange(value)}
                    data={[
                        { value: "5", label: "5 per page" },
                        { value: "10", label: "10 per page" },
                        { value: "20", label: "20 per page" },
                        { value: "50", label: "50 per page" },
                    ]}
                    disabled={isFiltered}
                    style={{ minWidth: 110 }}
                />
                {/* ── View toggle ── */}
                <SegmentedControl
                    value={viewMode}
                    onChange={onViewModeChange}
                    aria-label="Choose view mode"
                    size="xs"
                    color="brand"
                    data={[
                        {
                            value: "grid",
                            label: (
                                <Tooltip label="Card view" withArrow position="bottom">
                                    <Center>
                                        <IconSquares2X2
                                            style={{ width: rem(18), height: rem(18) }}
                                        />
                                    </Center>
                                </Tooltip>
                            ),
                        },
                        {
                            value: "list",
                            label: (
                                <Tooltip label="List view" withArrow position="bottom">
                                    <Center>
                                        <IconListBullet
                                            style={{ width: rem(18), height: rem(18) }}
                                        />
                                    </Center>
                                </Tooltip>
                            ),
                        },
                    ]}
                />
            </Group>

            <Table striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        {selectionMode && <Table.Th w={44} />}
                        <SortableTableHeader field="firstName" label="Name" />
                        <SortableTableHeader
                            field="availabilityDate"
                            label="Assignment"
                        />
                        <SortableTableHeader
                            field="availabilityDate"
                            label="Availability"
                        />
                        <Table.Th>Position</Table.Th>
                        <SortableTableHeader
                            field="officeLocation"
                            label="Office Location"
                        />
                        <SortableTableHeader
                            field="cvUpdateDate"
                            label="CV Status"
                        />
                        <SortableTableHeader
                            field="finalScoring"
                            label="CV Score"
                        />
                        <Table.Th>Action</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {persons.map((person) => (
                        <CVListTableRow
                            key={person.id}
                            person={person}
                            selectionMode={selectionMode}
                            isSelected={selectedIds?.has(person.id) || false}
                            onToggleSelect={onToggleSelect}
                            onSelectPerson={onSelectPerson}
                            onModifyPerson={onModifyPerson}
                            onDeletePerson={onDeletePerson}
                            onRefresh={onRefresh}
                        />
                    ))}
                </Table.Tbody>
            </Table>
        </Box>
    );
}

